import { StrategyApiUpdateRequest } from 'shared/src/api/strategy-api-types';
import { calcNumDays } from 'shared/src/utils/date/date-utils';
import { ISODateString } from 'shared/src/date-string-utils/types';
import { formatDateOrDash } from 'shared/src/date-string-utils/date-utils';
import {
  calcAgencyMarkupAmount,
  calcEffectivePrice,
  calcGrossPrice,
  calcReferralCommissionAmount,
  formatPercentage
} from 'shared/src/domain/line-item/line-item-utils';
import { Control, Controller, FieldPath, FieldPathByValue, useFormContext } from 'react-hook-form';
import React, { useRef } from 'react';
import clsx from 'clsx';
import { UTCDate } from '@date-fns/utc';
import { format, startOfDay } from 'date-fns';
import Cleave from 'cleave.js/react';
import { debounce } from 'lodash';
import { CampaignV2 } from 'shared/src/api/campaigns-v2';
import { formatCurrency } from '@/components/tables/table-utils';
import { StrategyTrpcResponse } from 'shared/src/trpc-types/strategy-trpc-types';

export function calculatedProposalValues(values: StrategyApiUpdateRequest) {
  const { price, start_date, end_date, referral_commission_rate, agency_markup_rate } = values;
  const numDays = start_date && end_date ? calcNumDays(start_date, end_date) : '-';

  // Calculate financial values
  // Create a temporary line item object for calculations
  const tempLineItem = {
    id: 'temp',
    strategy_id: 'temp',
    price,
    referral_commission_rate: referral_commission_rate ?? undefined,
    agency_markup_rate: agency_markup_rate ?? undefined
  };
  const referralFee = calcReferralCommissionAmount(tempLineItem);
  const effectivePrice = calcEffectivePrice(tempLineItem);
  const agencyMarkupAmount = calcAgencyMarkupAmount(tempLineItem, agency_markup_rate);
  const grossPrice = calcGrossPrice(tempLineItem, agency_markup_rate);

  return { numDays, referralFee, effectivePrice, agencyMarkupAmount, grossPrice };
}

type InputShellProps = {
  name: FieldPath<StrategyApiUpdateRequest>;
  label: string;
  children: React.ReactNode;
};

export function InputShell({ name, label, children }: InputShellProps) {
  const { formState } = useFormContext<StrategyApiUpdateRequest>();
  return (
    <div
      className={clsx(
        'flex w-[180px] items-center justify-between gap-1 rounded-sm bg-white px-2',
        formState.errors[name] && 'border border-red-500',
        formState.dirtyFields[name] && 'border border-orange-400'
      )}>
      <div className="whitespace-nowrap text-xs text-gray-600">{label}</div>
      {children}
    </div>
  );
}

type DateFieldProps = {
  name: FieldPathByValue<StrategyApiUpdateRequest, string | undefined>;
  min: UTCDate;
  max: UTCDate;
  type?: 'embedded' | 'standalone';
};

export function DateField({ name, min, max, type = 'embedded' }: DateFieldProps) {
  const { register } = useFormContext<StrategyApiUpdateRequest>();
  const { formState } = useFormContext<StrategyApiUpdateRequest>();

  return (
    <input
      {...register(name)}
      className={
        type === 'standalone'
          ? clsx(
              'min-w-0 rounded-sm px-4 py-1.5 text-right text-sm font-normal text-blue-600 focus:ring-0',
              formState.errors[name]
                ? 'border border-red-500 focus:border-red-500'
                : 'border-gray-300 focus:border-gray-300'
            )
          : 'min-w-0 border-none p-0 text-right text-sm font-normal text-blue-600 focus:ring-0'
      }
      id={name}
      type="date"
      min={format(startOfDay(min), 'yyyy-MM-dd')}
      max={format(startOfDay(max), 'yyyy-MM-dd')}
      onClick={e => e.currentTarget.showPicker && e.currentTarget.showPicker()}
    />
  );
}

type PriceInputProps = {
  control: Control<StrategyApiUpdateRequest>;
  type?: 'embedded' | 'standalone';
};

export function PriceInput({ control, type = 'embedded' }: PriceInputProps) {
  const { formState } = useFormContext<StrategyApiUpdateRequest>();

  return (
    <Controller
      control={control}
      name="price"
      render={({ field: { onChange, value } }) => (
        <Cleave
          className={
            type === 'standalone'
              ? clsx(
                  'min-w-0 rounded-sm px-4 py-1.5 text-right text-sm font-normal text-blue-600 focus:ring-0',
                  formState.errors.price
                    ? 'border border-red-500 focus:border-red-500'
                    : 'border-gray-300 focus:border-gray-300'
                )
              : 'min-w-0 border-none p-0 text-right text-sm font-normal text-blue-600 focus:ring-0'
          }
          options={{
            numeral: true,
            numeralThousandsGroupStyle: 'thousand',
            prefix: '$',
            rawValueTrimPrefix: true,
            numeralDecimalScale: 2
          }}
          value={value}
          onChange={e => {
            const rawValue = e.target.rawValue;
            const parsed = parseFloat(rawValue);
            onChange(isNaN(parsed) ? 0 : parsed);
          }}
        />
      )}
    />
  );
}

type PctInputProps = {
  name: FieldPathByValue<StrategyApiUpdateRequest, number | null | undefined>;
  control: Control<StrategyApiUpdateRequest>;
  type?: 'embedded' | 'standalone';
};

export function PctInput({ name, control, type = 'embedded' }: PctInputProps) {
  const { formState } = useFormContext<StrategyApiUpdateRequest>();
  const onChangeRef = useRef<(v: number) => void>(null);
  const debouncedUpdate = useRef(
    debounce((rawValue: string) => {
      const parsed = parseFloat(rawValue);
      onChangeRef.current?.(isNaN(parsed) ? 0 : parsed / 100);
    }, 500)
  ).current;
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value } }) => {
        onChangeRef.current = onChange;
        return (
          <Cleave
            className={
              type === 'standalone'
                ? clsx(
                    'min-w-0 rounded-sm px-4 py-1.5 text-right text-sm font-normal text-blue-600 focus:ring-0',
                    formState.errors[name]
                      ? 'border border-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:border-gray-300'
                  )
                : 'min-w-0 border-none p-0 text-right text-sm font-normal text-blue-600 focus:ring-0'
            }
            options={{
              numeral: true,
              numeralThousandsGroupStyle: 'thousand',
              rawValueTrimPrefix: true,
              numeralDecimalScale: 2
            }}
            value={value ? Math.round(value * 10000) / 100 : 0}
            onChange={e => debouncedUpdate(e.target.rawValue)}
            onBlur={() => debouncedUpdate.flush()}
            onKeyDown={e => {
              if (e.key === 'Enter') debouncedUpdate.flush();
            }}
          />
        );
      }}
    />
  );
}

export function NumDays({ numDays }: { numDays: number | string }) {
  return (
    <div className="flex items-center gap-2 rounded-sm bg-gray-50 px-2 py-1">
      <div className="text-xs text-gray-600">Days</div>
      <div className="text-sm font-semibold">{numDays}</div>
    </div>
  );
}

type ViewFieldProps = {
  label: string;
  value: string;
};

export function ViewField({ label, value }: ViewFieldProps) {
  return (
    <div className="flex w-[180px] items-center justify-between gap-1 rounded-sm bg-white px-2 py-1">
      <div className="text-xs text-gray-600">{label}</div>
      <div className="text-right text-sm font-semibold">{value}</div>
    </div>
  );
}

export function shouldShowRefRate(strategy: StrategyTrpcResponse, campaign: CampaignV2) {
  return (
    strategy.referral_commission_rate > 0 ||
    (campaign.referral_partner_comm != null && campaign.referral_partner_comm > 0)
  );
}

export function shouldShowAgency(strategy: StrategyTrpcResponse, campaign: CampaignV2): boolean {
  return (
    strategy.agency_markup_rate > 0 ||
    (campaign.agency_gross_markup != null && campaign.agency_gross_markup > 0)
  );
}

export function formatCurrSafe(value: number | null | undefined) {
  return value != null ? formatCurrency(value) : '$0.00';
}

export function formatPctSafe(value: number | null | undefined) {
  return value ? formatPercentage(value, 2) : '0.00%';
}

export function formatDateSafe(date: ISODateString | null | undefined) {
  return formatDateOrDash(date);
}
